/**
 * StockAndDwellPage — three operator-facing reports under one page:
 *
 *   1. Physical Stock — every bale physically inside the mill (Under Inspection
 *      + Approved + Hold + Rejected-still-on-premises). For insurance / fire-
 *      risk / godown audit / security register.
 *
 *   2. Available Stock — only QC-approved bales usable for mixing.
 *      For production planning.
 *
 *   3. Truck Dwell Time — entry → gross → tare → exit timestamps per truck,
 *      with average dwell + average unloading time. Helps the floor monitor
 *      how long trucks sit inside.
 *
 * All three are derived views — no new tables, no migration.
 */
import { useState } from 'react'
import {
  Card, Col, DatePicker, Empty, Row, Select, Space, Spin,
  Statistic, Table, Tabs, Tag, Typography,
} from 'antd'
import {
  AlertOutlined, CarOutlined, DatabaseOutlined,
  FundProjectionScreenOutlined, SafetyOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const NUM = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' as const }

const fmt = (v: string | number | null | undefined, dp = 2) =>
  v == null ? '—' : Number(v).toLocaleString('en-IN',
    { minimumFractionDigits: dp, maximumFractionDigits: dp })

export default function StockAndDwellPage() {
  const { plantId, plants, setPlantId } = usePlantId()

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <SafetyOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Stock &amp; Truck Dwell Time
      </Title>
      <Text type="secondary">
        Physical stock for insurance · Available stock for mixing · Truck
        dwell time for floor efficiency.
      </Text>

      <Card style={{ marginTop: 16 }} styles={{ body: { padding: 16 } }}>
        <Row style={{ marginBottom: 16 }}>
          <Col xs={24} md="auto">
            <Select value={plantId} onChange={setPlantId}
              style={{ width: 280 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))} />
          </Col>
        </Row>

        <Tabs
          defaultActiveKey="physical"
          items={[
            { key: 'physical', label: <span><DatabaseOutlined /> Physical Stock</span>,
              children: <PhysicalStockTab plantId={plantId} /> },
            { key: 'available', label: <span><FundProjectionScreenOutlined /> Available Stock</span>,
              children: <AvailableStockTab plantId={plantId} /> },
            { key: 'dwell', label: <span><CarOutlined /> Truck Dwell Time</span>,
              children: <DwellTimeTab plantId={plantId} /> },
          ]}
        />
      </Card>
    </div>
  )
}

// ─── Physical Stock ─────────────────────────────────────────────────────
interface PhysicalResponse {
  total_bales: number
  total_weight_kg: number
  total_value: number
  by_qc_status: { qc_status: string; bales: number; weight_kg: number; value: number }[]
  by_godown: { godown: string; bales: number; weight_kg: number; value: number }[]
}

function PhysicalStockTab({ plantId }: { plantId: string }) {
  const { data, isLoading } = useQuery<PhysicalResponse>({
    queryKey: ['physical-stock', plantId],
    queryFn: () => apiClient.get('/reports/stock/physical',
      { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  if (isLoading) return <Spin />
  if (!data) return <Empty />

  const qcColor = (s: string) =>
    s === 'APPROVED' ? 'success'
    : s === 'HOLD' ? 'warning'
    : s === 'REJECTED' ? 'error'
    : 'processing'  // PENDING / others

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Bales physically present"
              value={data.total_bales} valueStyle={{ ...NUM }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Total weight (kg)"
              value={data.total_weight_kg} precision={0}
              valueStyle={{ ...NUM }} />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card size="small" style={{ background: '#fff7e6' }}>
            <Statistic title="Insurance value (₹)"
              value={data.total_value} precision={0} prefix="₹"
              valueStyle={{ ...NUM, color: '#fa8c16' }} />
          </Card>
        </Col>
      </Row>

      <Card size="small" title="By QC status" style={{ marginBottom: 12 }}>
        <Table size="small" pagination={false} rowKey="qc_status"
          dataSource={data.by_qc_status}
          columns={[
            { title: 'Status', dataIndex: 'qc_status',
              render: (v: string) => <Tag color={qcColor(v)}>{v}</Tag> },
            { title: 'Bales', dataIndex: 'bales', align: 'right' as const },
            { title: 'Weight (kg)', dataIndex: 'weight_kg', align: 'right' as const,
              render: (v: number) => fmt(v, 0) },
            { title: 'Value ₹', dataIndex: 'value', align: 'right' as const,
              render: (v: number) => fmt(v, 0) },
          ]}
        />
      </Card>

      <Card size="small" title="By godown / location">
        <Table size="small" pagination={false} rowKey="godown"
          dataSource={data.by_godown}
          columns={[
            { title: 'Godown', dataIndex: 'godown' },
            { title: 'Bales', dataIndex: 'bales', align: 'right' as const },
            { title: 'Weight (kg)', dataIndex: 'weight_kg', align: 'right' as const,
              render: (v: number) => fmt(v, 0) },
            { title: 'Value ₹', dataIndex: 'value', align: 'right' as const,
              render: (v: number) => fmt(v, 0) },
          ]}
        />
      </Card>
    </>
  )
}

// ─── Available Stock ────────────────────────────────────────────────────
interface AvailableResponse {
  total_bales: number
  total_weight_kg: number
  total_value: number
  by_lot: {
    lot_no: string; bales: number; weight_kg: number;
    value: number; godowns: string[]
  }[]
}

function AvailableStockTab({ plantId }: { plantId: string }) {
  const { data, isLoading } = useQuery<AvailableResponse>({
    queryKey: ['available-stock', plantId],
    queryFn: () => apiClient.get('/reports/stock/available',
      { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  if (isLoading) return <Spin />
  if (!data) return <Empty />

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={8}>
          <Card size="small" style={{ background: '#f6ffed' }}>
            <Statistic title="QC-approved bales (mixable)"
              value={data.total_bales}
              valueStyle={{ ...NUM, color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={12} md={8}>
          <Card size="small">
            <Statistic title="Available weight (kg)"
              value={data.total_weight_kg} precision={0}
              valueStyle={{ ...NUM }} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic title="Available value (₹)"
              value={data.total_value} precision={0} prefix="₹"
              valueStyle={{ ...NUM }} />
          </Card>
        </Col>
      </Row>

      <Card size="small" title="By Mill Lot">
        <Table size="small" pagination={{ pageSize: 25 }}
          dataSource={data.by_lot} rowKey="lot_no"
          scroll={{ x: 'max-content' }}
          columns={[
            { title: 'Lot No', dataIndex: 'lot_no',
              render: (v: string) => <Text strong style={{ fontFamily: 'monospace' }}>{v}</Text> },
            { title: 'Bales', dataIndex: 'bales', align: 'right' as const },
            { title: 'Weight (kg)', dataIndex: 'weight_kg', align: 'right' as const,
              render: (v: number) => fmt(v, 0) },
            { title: 'Value ₹', dataIndex: 'value', align: 'right' as const,
              render: (v: number) => fmt(v, 0) },
            { title: 'Godowns', dataIndex: 'godowns',
              render: (v: string[]) => v.length > 0
                ? v.map((g, i) => <Tag key={i}>{g}</Tag>)
                : '—' },
          ]}
        />
      </Card>
    </>
  )
}

// ─── Truck Dwell Time ───────────────────────────────────────────────────
interface DwellRow {
  inward_no: string; vehicle_no: string; supplier_name: string
  entry_time: string | null
  gross_at: string | null; tare_at: string | null
  exit_time: string | null
  gross_weight_kg: number | null; tare_weight_kg: number | null
  net_weight_kg: number | null
  unloading_minutes: number | null
  dwell_minutes: number | null
  current_status: string
}
interface DwellResponse {
  total_trucks: number; completed_trucks: number
  avg_dwell_minutes: number | null; avg_unloading_minutes: number | null
  max_dwell_minutes: number | null; max_dwell_inward_no: string
  rows: DwellRow[]
}

function DwellTimeTab({ plantId }: { plantId: string }) {
  const [from, setFrom] = useState<Dayjs>(dayjs().subtract(7, 'day'))
  const [to, setTo] = useState<Dayjs>(dayjs())

  const { data, isLoading } = useQuery<DwellResponse>({
    queryKey: ['truck-dwell', plantId, from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD')],
    queryFn: () => apiClient.get('/reports/truck-dwell-time', {
      params: {
        plant_id: plantId,
        date_from: from.format('YYYY-MM-DD'),
        date_to: to.format('YYYY-MM-DD'),
      },
    }).then(r => r.data),
    enabled: !!plantId,
  })

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <DatePicker.RangePicker value={[from, to]}
            onChange={(v) => { if (v?.[0]) setFrom(v[0]); if (v?.[1]) setTo(v[1]) }}
            style={{ width: '100%' }} />
        </Col>
      </Row>

      {isLoading && <Spin />}
      {!isLoading && data && (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Trucks in period"
                  value={data.total_trucks} valueStyle={{ ...NUM }} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Completed (gate→exit)"
                  value={data.completed_trucks} valueStyle={{ ...NUM }} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" style={{ background: '#e6f4ff' }}>
                <Statistic title="Avg dwell (min)"
                  value={data.avg_dwell_minutes ?? '—'}
                  precision={data.avg_dwell_minutes != null ? 1 : 0}
                  valueStyle={{ ...NUM, color: '#1677ff' }} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" style={{ background: '#f6ffed' }}>
                <Statistic title="Avg unloading (gross→tare)"
                  value={data.avg_unloading_minutes ?? '—'}
                  precision={data.avg_unloading_minutes != null ? 1 : 0}
                  suffix="min"
                  valueStyle={{ ...NUM, color: '#52c41a' }} />
              </Card>
            </Col>
          </Row>

          {data.max_dwell_minutes != null && (
            <Card size="small" style={{ marginBottom: 12, background: '#fff1f0' }}>
              <Space>
                <AlertOutlined style={{ color: '#cf1322' }} />
                <Text strong>Longest dwell:</Text>
                <Text>{data.max_dwell_inward_no} — {fmt(data.max_dwell_minutes, 1)} min</Text>
              </Space>
            </Card>
          )}

          <Table size="small" dataSource={data.rows} rowKey="inward_no"
            pagination={{ pageSize: 25 }}
            scroll={{ x: 'max-content' }}
            columns={[
              { title: 'Inward', dataIndex: 'inward_no',
                render: (v: string) => <Text strong style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text> },
              { title: 'Vehicle', dataIndex: 'vehicle_no' },
              { title: 'Supplier', dataIndex: 'supplier_name', ellipsis: true },
              { title: 'Entered', dataIndex: 'entry_time',
                render: (v: string | null) => v ? dayjs(v).format('DD-MMM HH:mm') : '—' },
              { title: 'Gross at', dataIndex: 'gross_at',
                render: (v: string | null) => v ? dayjs(v).format('HH:mm') : <Tag>pending</Tag> },
              { title: 'Tare at', dataIndex: 'tare_at',
                render: (v: string | null) => v ? dayjs(v).format('HH:mm') : <Tag>pending</Tag> },
              { title: 'Exit', dataIndex: 'exit_time',
                render: (v: string | null) => v ? dayjs(v).format('HH:mm') : <Tag color="processing">on premises</Tag> },
              { title: 'Unloading min', dataIndex: 'unloading_minutes', align: 'right' as const,
                render: (v: number | null) => v != null
                  ? <span style={NUM}>{fmt(v, 1)}</span>
                  : <Text type="secondary">—</Text> },
              { title: 'Dwell min', dataIndex: 'dwell_minutes', align: 'right' as const,
                render: (v: number | null) => v != null
                  ? <Text strong style={NUM}>{fmt(v, 1)}</Text>
                  : <Text type="secondary">—</Text> },
              { title: 'Status', dataIndex: 'current_status',
                render: (v: string) => <Tag>{v}</Tag> },
            ]}
          />
        </>
      )}
    </>
  )
}
